import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { userApi } from '../api/axios';
import {
  ClientSession,
  effacerSession,
  enregistrerSession,
  expirationDuJeton,
  lireSession,
} from '../auth/session';

type User = ClientSession;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  /** Vrai quand la session vient d'expirer pendant que le client était sur le portail. */
  sessionExpiree: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Création de compte sur invitation : le code part dans le corps (user-service, a370081). */
  register: (d: { email: string; password: string; nom: string; organisation: string; invitation: string }) => Promise<void>;
  logout: () => Promise<void>;
  /** Reporte dans la session un profil modifié (nom, organisation). */
  actualiserProfil: (p: { nom?: string | null; organisation?: string | null }) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

/* setTimeout ne dépasse pas 2^31 - 1 ms (24,8 jours) : au-delà, on se réveille avant. */
const DELAI_MAX = 2_147_483_647;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Lecture synchrone : la session est là dès le premier rendu, un rechargement
  // de page ne passe plus par la connexion.
  const [session, setSession] = useState(() => lireSession());
  const [sessionExpiree, setSessionExpiree] = useState(false);

  const expirer = useCallback(() => {
    effacerSession();
    setSession(null);
    setSessionExpiree(true);
  }, []);

  // Expiration propre : à l'heure exacte du jeton, retour à la connexion.
  useEffect(() => {
    if (!session) return;
    const exp = expirationDuJeton(session.jeton);
    if (exp == null) return;
    let minuteur: ReturnType<typeof setTimeout>;
    const programmer = () => {
      const reste = exp - 30_000 - Date.now();
      if (reste <= 0) { expirer(); return; }
      minuteur = setTimeout(programmer, Math.min(reste, DELAI_MAX));
    };
    programmer();
    return () => clearTimeout(minuteur);
  }, [session, expirer]);

  // Déconnexion ou reconnexion dans un autre onglet.
  useEffect(() => {
    const surStockage = (e: StorageEvent) => {
      if (e.key === 'clientToken' || e.key === 'clientUser' || e.key === null) setSession(lireSession());
    };
    window.addEventListener('storage', surStockage);
    return () => window.removeEventListener('storage', surStockage);
  }, []);

  const login = async (email: string, password: string) => {
    const res = await userApi.post('/user/client-login', { email, password });
    const { email: resEmail, nom, token, organisation } = res.data ?? {};
    const u: User = {
      email: resEmail || email,
      nom: nom || '',
      organisation: organisation || '',
    };
    // Requis par location-service et admin-service, que le cookie host-only n'atteint pas.
    if (typeof token !== 'string' || !token) throw new Error('Jeton absent de la réponse de connexion');
    enregistrerSession(u, token);
    setSessionExpiree(false);
    setSession({ utilisateur: u, jeton: token });
  };

  const register = async (d: { email: string; password: string; nom: string; organisation: string; invitation: string }) => {
    await userApi.post('/user/client-register', d);
  };

  const logout = async () => {
    try { await userApi.post('/user/client-logout'); } catch { /* ignorer */ }
    effacerSession();
    setSessionExpiree(false);
    setSession(null);
  };

  const actualiserProfil = (p: { nom?: string | null; organisation?: string | null }) => {
    setSession(s => {
      if (!s) return s;
      const utilisateur = { ...s.utilisateur, nom: p.nom ?? s.utilisateur.nom, organisation: p.organisation ?? '' };
      enregistrerSession(utilisateur, s.jeton);
      return { ...s, utilisateur };
    });
  };

  const user = session?.utilisateur ?? null;

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, sessionExpiree, login, register, logout, actualiserProfil }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
